// ============================================================
// Attendance Store — 出勤打卡狀態管理
// ============================================================

import { create } from 'zustand';
import { api } from '../lib/api';

export interface AttendanceRecord {
  id: string;
  userId: string;
  user?: { id: string; name: string; avatar?: string };
  schedule?: { startTime: string; endTime: string; position?: string; breakStart?: string | null; breakEnd?: string | null };
  clockIn?: string;
  clockOut?: string;
  clockInMethod?: string;
  breakOut?: string | null;   // 休息開始打卡
  breakIn?: string | null;    // 休息結束打卡
  status: 'NORMAL' | 'LATE' | 'EARLY_LEAVE' | 'MISSED' | 'PENDING';
  approvalStatus: 'AUTO_APPROVED' | 'PENDING' | 'APPROVED' | 'REJECTED';
  note?: string;
}

export type ClockState = 'NOT_STARTED' | 'WORKING' | 'ON_BREAK' | 'RETURNED' | 'FINISHED';

interface ClockPayload {
  method: 'QR' | 'WIFI' | 'GPS';
  qrToken?: string;
  wifiSSID?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number; // 手機回報的定位誤差（公尺）
  note?: string; // 遲到 / 早退原因
}

interface AttendanceState {
  todayRecords: AttendanceRecord[];
  pendingRecords: AttendanceRecord[];
  isClocking: boolean;
  clockResult: { success: boolean; message: string } | null;
  isLoading: boolean;

  fetchToday: (date?: string) => Promise<void>;
  fetchPending: () => Promise<void>;
  clockIn: (payload: ClockPayload) => Promise<void>;
  clockOut: (payload: ClockPayload) => Promise<void>;
  breakOut: (payload: ClockPayload) => Promise<void>;
  breakIn: (payload: ClockPayload) => Promise<void>;
  approve: (id: string, action: 'APPROVED' | 'REJECTED', note?: string) => Promise<void>;
  clearClockResult: () => void;
}

export const useAttendanceStore = create<AttendanceState>((set, get) => ({
  todayRecords: [],
  pendingRecords: [],
  isClocking: false,
  clockResult: null,
  isLoading: false,

  fetchToday: async (date) => {
    set({ isLoading: true });
    try {
      // 用 Taipei 日期（修 bug：toISOString() 是 UTC 日期，
      // 半夜 0-8 點 Taipei 會抓到前一天的紀錄）
      const dateParam = date ?? new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
      const data = await api.get<{ records: AttendanceRecord[] }>(
        `/attendance?date=${dateParam}`
      );
      set({ todayRecords: data.records, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  fetchPending: async () => {
    set({ isLoading: true });
    try {
      const data = await api.get<{ records: AttendanceRecord[] }>('/attendance/pending');
      set({ pendingRecords: data.records, isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  clockIn: async (payload) => {
    set({ isClocking: true, clockResult: null });
    try {
      const data = await api.post<{ attendance: AttendanceRecord; message: string }>(
        '/attendance/clock-in',
        payload
      );
      set({
        isClocking: false,
        clockResult: { success: true, message: data.message },
      });
      get().fetchToday();
    } catch (err) {
      set({
        isClocking: false,
        clockResult: { success: false, message: (err as Error).message },
      });
    }
  },

  clockOut: async (payload) => {
    set({ isClocking: true, clockResult: null });
    try {
      const data = await api.post<{ attendance: AttendanceRecord; message: string }>(
        '/attendance/clock-out',
        payload
      );
      set({
        isClocking: false,
        clockResult: { success: true, message: data.message },
      });
      get().fetchToday();
    } catch (err) {
      set({
        isClocking: false,
        clockResult: { success: false, message: (err as Error).message },
      });
    }
  },

  breakOut: async (payload) => {
    set({ isClocking: true, clockResult: null });
    try {
      const data = await api.post<{ attendance: AttendanceRecord; message: string }>(
        '/attendance/break-out', payload
      );
      set({ isClocking: false, clockResult: { success: true, message: data.message } });
      get().fetchToday();
    } catch (err) {
      set({ isClocking: false, clockResult: { success: false, message: (err as Error).message } });
    }
  },

  breakIn: async (payload) => {
    set({ isClocking: true, clockResult: null });
    try {
      const data = await api.post<{ attendance: AttendanceRecord; message: string }>(
        '/attendance/break-in', payload
      );
      set({ isClocking: false, clockResult: { success: true, message: data.message } });
      get().fetchToday();
    } catch (err) {
      set({ isClocking: false, clockResult: { success: false, message: (err as Error).message } });
    }
  },

  approve: async (id, action, note) => {
    try {
      await api.put(`/attendance/${id}/approve`, { action, note });
      set((state) => ({
        pendingRecords: state.pendingRecords.filter((r) => r.id !== id),
      }));
    } catch (err) {
      throw err;
    }
  },

  clearClockResult: () => set({ clockResult: null }),
}));
